import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Checkbox,
  Icon,
} from "@elaniintc/goes-design-system";
import type { HolderProcessor } from "@/queries/data-center/data-center.api";
import EditableCell from "./EditableCell";
import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

const MAX_COLUMNS = 50;

type Direction = "asc" | "desc" | "off";
interface ColumnHeaderProps {
  id: string;
  title: string;
  type: string;
  canEdit?: boolean;
  onEditColumn: () => void;
  sortState: {
    columnId: string;
    direction: Direction;
  } | null;
  onSort: (columnId: string, direction: Direction) => void;
}

const ColumnHeader = ({
  id,
  title,
  type,
  canEdit,
  sortState,
  onEditColumn,
  onSort,
}: ColumnHeaderProps) => {
  const getColumnType = (type: string) => {
    switch (type.toLowerCase()) {
      case "string":
        return "texto";
      case "integer":
        return "número";
      case "float":
        return "decimal";
      case "int8":
        return "INT8";
      case "float8":
        return "FLOAT8";
      case "boolean":
        return "verdadero/falso";
      default:
        return type.toUpperCase();
    }
  };

  const getSortIcon = () => {
    if (!sortState) return "sort";
    if (sortState.direction === "asc" && sortState.columnId === id)
      return "sort-up";
    if (sortState.direction === "desc" && sortState.columnId === id)
      return "sort-down";
    return "sort";
  };

  return (
    <th className="text-left bg-neutral-object-accent-silent border-b border-l border-neutral py-2 px-3">
      <div className="w-full justify-between flex items-center gap-4">
        <div className="flex flex-col">
          <p className="text-xs text-body-loud uppercase">{title}</p>
          <span className="text-xs text-body-quiet font-normal">
            {getColumnType(type)}
          </span>
        </div>
        <div className="flex gap-4">
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="w-6 h-6 p-0"
              aria-label="Editar columna"
              onClick={onEditColumn}
            >
              <Icon type="far" name="ellipsis-vertical" />
            </Button>
          )}
          <button
            type="button"
            className="w-3 cursor-pointer"
            onClick={() => {
              if (!sortState) {
                onSort(id, "asc");
                return;
              }
              if (sortState?.columnId === id) {
                if (sortState.direction === "asc") {
                  onSort(id, "desc");
                } else if (sortState.direction === "desc") {
                  onSort(id, "off");
                } else {
                  onSort(id, "asc");
                }
              }
            }}
          >
            <Icon
              type="fas"
              name={getSortIcon()}
              className="text-on-disabled"
            />
          </button>
        </div>
      </div>
    </th>
  );
};

interface TableProps {
  columns: {
    name: string;
    type: string;
    id: string;
    display_name: string;
  }[];
  rows: {
    id: string;
    "row-key": string;
    created_at: string;
    updated_at: string;
    originalIndex: number;
    source?: string;
    is_institution?: boolean;
    holder?: HolderProcessor | null;
    processor?: HolderProcessor | null;
    [key: string]: unknown;
  }[];
  canEditTable: boolean;
  canCreateRegistry: boolean;
  canEditRegistry: boolean;
  onNewColumn?: () => void;
  shouldAllowNewColumn?: boolean;
  onAddData: (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => void;
  onSelectRow: (
    val: { id: string; rowId: string },
    isSelected: boolean,
  ) => void;
  areAllRowsSelected: boolean;
  selectedRows?: {
    id: string;
    rowId: string;
  }[];
  onSelectAllRows: () => void;
  onEditColumn: (columnId: string) => void;
  sortState: {
    columnId: string;
    direction: "asc" | "desc" | "off";
  } | null;
  onSort: (columnId: string, direction: "asc" | "desc" | "off") => void;
  hasMoreData?: boolean;
  isLoadingMoreData?: boolean;
  onLoadMore?: () => void;
}

const Table = ({
  onNewColumn,
  columns,
  rows,
  shouldAllowNewColumn,
  onAddData,
  areAllRowsSelected,
  onSelectRow,
  selectedRows,
  onSelectAllRows,
  onEditColumn,
  onSort,
  canCreateRegistry,
  canEditTable,
  canEditRegistry,
  sortState,
  hasMoreData,
  isLoadingMoreData,
  onLoadMore,
}: TableProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: hasMoreData ? rows.length + 1 : rows.length,
    //getScrollElement: () => parentRef || null,
    getScrollElement: () => ref.current,
    estimateSize: () => 45,
    overscan: 53,
  });

  return (
    <div className="w-full overflow-x-auto pb-10" ref={ref}>
      <table className="w-full border-collapse border-b border-neutral">
        <thead>
          <tr>
            <th
              className={cn(
                "text-left bg-neutral-object-accent-silent border-b border-neutral py-2 px-3",
                columns.length === 0 ? "w-full" : "w-31",
              )}
            >
              <div className="flex items-center gap-4">
                {canEditRegistry && (
                  <Checkbox
                    id="select-all-checkbox"
                    icon="-"
                    checked={areAllRowsSelected} // Implement selection logic
                    onChange={() => {
                      if (onSelectAllRows) {
                        onSelectAllRows();
                      }
                      /* Implement select all logic */
                    }}
                  />
                )}
                <div className="flex flex-col">
                  <span className="font-semibold text-xs text-body-loud">
                    INDEX
                  </span>
                  <span className="text-xs text-body-quiet font-normal">
                    int8
                  </span>
                </div>
              </div>
            </th>
            <th className="text-left bg-neutral-object-accent-silent border-b border-l border-neutral py-2 px-3 min-w-48">
              <div className="flex flex-col">
                <p className="text-xs text-body-loud uppercase">Creado por</p>
                <span className="text-xs text-body-quiet font-normal">—</span>
              </div>
            </th>
            {columns.map(
              (
                col: {
                  name: string;
                  type: string;
                  id: string;
                  display_name: string;
                },
                idx: number,
              ) => (
                <ColumnHeader
                  key={`col-header-${col.id}-${idx}`}
                  title={col.display_name || col.name}
                  canEdit={shouldAllowNewColumn && canEditTable}
                  type={col.type.toLowerCase()}
                  onEditColumn={() => onEditColumn(col.id)}
                  id={col.name}
                  sortState={sortState}
                  onSort={(columnId, direction) => onSort(columnId, direction)}
                />
              ),
            )}
            {columns.length < MAX_COLUMNS &&
              shouldAllowNewColumn &&
              canEditTable && (
                <th className="text-left bg-neutral-object-accent-silent border-b border-l border-neutral py-2 px-3 w-14">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Añadir columna"
                    onClick={onNewColumn}
                  >
                    <Icon type="far" name="plus" />
                  </Button>
                </th>
              )}
          </tr>
        </thead>
        <tbody>
          {virtualizer.getVirtualItems()?.map((virtualRow, index) => {
            const row = rows[virtualRow.index];

            if (!row) return null;

            const isSelected = selectedRows?.some(
              (selectedRow) =>
                selectedRow.id === row.id ||
                selectedRow.rowId === row["row-key"],
            );
            return (
              <tr
                key={`row-${row.id || row["row-key"]}-${index}`}
                className={cn("bg-neutral-object")}
                style={{
                  height: virtualRow.size,
                  //transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <td
                  className={cn(
                    "border-b border-r border-neutral p-3 text-sm",
                    isSelected
                      ? "bg-surface-action-accent-quiet"
                      : "bg-neutral-object-accent-silent",
                  )}
                >
                  <div className="flex items-center gap-4">
                    {canEditRegistry && (
                      <Checkbox
                        id={`row-checkbox-${index}`}
                        icon="check"
                        checked={isSelected} // Implement row selection logic
                        onChange={() => {
                          onSelectRow(
                            {
                              id: row.id,
                              rowId: row["row-key"],
                            },
                            !isSelected,
                          );
                        }}
                      />
                    )}
                    <span className="text-body-loud text-xs">{index + 1}</span>
                  </div>
                </td>
                <td className="border-b border-l border-neutral p-3 text-sm">
                  <div className="flex items-center gap-4 min-w-0">
                    <Avatar size="xs">
                      <AvatarFallback color="action">
                        {(row.holder as HolderProcessor)?.category ===
                          "LEGAL_PERSON" || row.is_institution ? (
                          <Icon
                            type="fas"
                            name={
                              row.is_institution
                                ? "building-shield"
                                : "buildings"
                            }
                            className="text-action-quiet"
                          />
                        ) : (
                          ((row.holder as HolderProcessor)?.name?.charAt(0) ??
                          "")
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col leading-tight min-w-0 overflow-hidden">
                      <p className="text-body text-base truncate">
                        {row.is_institution
                          ? "Institución"
                          : ((row.holder as HolderProcessor)?.name ?? "—")}
                      </p>
                      {row.processor &&
                        !row.is_institution &&
                        row.processor?.id !== row.holder?.id && (
                          <p className="text-body-quiet text-xs truncate">
                            Representado por {row.processor?.name}
                          </p>
                        )}
                    </div>
                  </div>
                </td>
                {columns.map(
                  (
                    col: { name: string; id: string; type: string },
                    idx: number,
                  ) => (
                    <EditableCell
                      key={`${col.id}-${index}-${idx}-${row["row-key"]}`}
                      type={col.type || "STRING"}
                      colName={col.name}
                      canEditRegistry={canEditRegistry}
                      isSelected={isSelected}
                      stripped={index % 2 !== 0}
                      rowIndex={row.originalIndex}
                      onAddData={onAddData}
                      canCreateRegistry={canCreateRegistry}
                      shouldRenderRightBorder={!row.id || shouldAllowNewColumn}
                    />
                  ),
                )}
              </tr>
            );
          })}
          {hasMoreData && !isLoadingMoreData && (
            <tr>
              <td
                colSpan={columns.length + 2}
                className="text-center py-4 text-body-quiet"
              >
                <button
                  className="w-ful h-full cursor-pointer"
                  type="button"
                  onClick={onLoadMore}
                >
                  Cargando más datos
                </button>
              </td>
            </tr>
          )}
          {isLoadingMoreData && (
            <tr>
              <td
                colSpan={columns.length + 2}
                className="text-center py-4 text-body-quiet"
              >
                <Icon
                  type="far"
                  name="spinner-third"
                  className="animate-spin text-sm text-current mr-2"
                />
                Cargando más datos...
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
